import React, { useState, useEffect, useMemo } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Link, useNavigate } from "react-router-dom";
import { 
  Card, 
  Table, 
  Row, 
  Col, 
  Form, 
  InputGroup, 
  Button, 
  Pagination, 
  Badge, 
  Modal, 
  Alert, 
  Spinner, 
  Toast, 
  ToastContainer,
  ProgressBar,
  ListGroup,
  Dropdown
} from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import {
  ClockHistory,
  Search,
  Eye,
  Download,
  Trash,
  ExclamationCircle,
  CheckCircle,
  XCircle,
  InfoCircle,
  Person,
  Cash,
  Calendar,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Printer,
  Share,
  Activity,
  Database,
  ShieldCheck,
  ShieldExclamation,
  Key,
  People,
  Gear,
  FileText,
  X,
  ArrowRepeat,
  FiletypeCsv,
  FiletypeJson,
  FilePdf
} from "react-bootstrap-icons";

// Configuration API
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Données de démo pour les logs (fallback)
const demoLogs = [];

// Options de filtre date
const dateFilters = [
  { value: "today", label: "Aujourd'hui" },
  { value: "yesterday", label: "Hier" },
  { value: "this_week", label: "Cette semaine" },
  { value: "last_week", label: "Semaine dernière" },
  { value: "this_month", label: "Ce mois" },
  { value: "last_month", label: "Mois dernier" },
  { value: "all", label: "Toutes les dates" },
  { value: "custom", label: "Personnalisé" }
];

export default function LogsPage() {
  const navigate = useNavigate();
  
  // États principaux
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useMockData, setUseMockData] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  
  // États pour les filtres
  const [selectedType, setSelectedType] = useState('all');
  const [selectedModule, setSelectedModule] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  
  // États pour le filtre date
  const [dateFilter, setDateFilter] = useState('today');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [showCustomDate, setShowCustomDate] = useState(false);
  
  // États pour les modals
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [logDetails, setLogDetails] = useState(null);
  const [showClearModal, setShowClearModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  
  // États pour l'export
  const [exportFormat, setExportFormat] = useState('csv');
  
  // États pour les notifications
  const [notifications, setNotifications] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });

  // Pagination API
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  });
  const [itemsPerPage, setItemsPerPage] = useState(15);

  // Types de logs
  const logTypes = [
    { value: "all", label: "Tous les types" },
    { value: "success", label: "Succès", color: "success", icon: <CheckCircle /> },
    { value: "danger", label: "Erreurs", color: "danger", icon: <XCircle /> },
    { value: "warning", label: "Avertissements", color: "warning", icon: <ExclamationCircle /> },
    { value: "info", label: "Informations", color: "info", icon: <InfoCircle /> }
  ];

  // Modules
  const modules = [
    { value: "all", label: "Tous les modules" },
    { value: "authentification", label: "Authentification", icon: <Key /> },
    { value: "taxes", label: "Taxes", icon: <Cash /> },
    { value: "paiements", label: "Paiements", icon: <FileText /> },
    { value: "contribuables", label: "Contribuables", icon: <People /> },
    { value: "rapports", label: "Rapports", icon: <Database /> },
    { value: "securite", label: "Sécurité", icon: <ShieldCheck /> },
    { value: "systeme", label: "Système", icon: <Gear /> },
    { value: "utilisateurs", label: "Utilisateurs", icon: <Person /> }
  ];

  // Obtenir le token d'authentification
  const getAuthToken = () => {
    const token = sessionStorage.getItem("authToken");
    return token;
  };

  // Headers pour les requêtes API
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  };

  // Fonction utilitaire pour extraire les infos du navigateur
  const extractBrowserInfo = (userAgent) => {
    if (!userAgent) return 'Inconnu';
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
    if (userAgent.includes('Edg')) return 'Edge';
    if (userAgent.includes('MSIE') || userAgent.includes('Trident')) return 'Internet Explorer';
    return 'Autre';
  };

  // Déterminer le type de log basé sur le code de réponse
  const determineLogType = (responseCode) => {
    if (responseCode >= 200 && responseCode < 300) return 'success';
    if (responseCode >= 400 && responseCode < 500) return 'warning';
    if (responseCode >= 500) return 'danger';
    return 'info';
  };

  // Déterminer le module basé sur le path
  const determineModule = (path) => {
    if (!path) return 'systeme';
    if (path.includes('auth') || path.includes('login') || path.includes('logout')) return 'authentification';
    if (path.includes('taxe')) return 'taxes';
    if (path.includes('paiement')) return 'paiements';
    if (path.includes('contribuable')) return 'contribuables';
    if (path.includes('rapport')) return 'rapports';
    if (path.includes('user') || path.includes('utilisateur')) return 'utilisateurs';
    if (path.includes('securite') || path.includes('security')) return 'securite';
    return 'systeme';
  };

  // Transformer les données de l'API
  const transformApiData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];
    return apiData.map(log => {
      const type = determineLogType(log.response_code);
      const module = determineModule(log.path);
      
      let description = log.response_body?.message || 
                       log.response_body?.error || 
                       `Requête ${log.method} sur ${log.path}`;
      
      if (typeof description === 'object') {
        description = JSON.stringify(description);
      }
      
      return {
        id: log.id,
        action: `${log.method} ${log.path}`,
        description: description.substring(0, 200),
        type: type,
        date: log.created_at,
        utilisateur: log.user_id ? `User #${log.user_id}` : 'système',
        ip: log.ip_address || 'N/A',
        navigateur: extractBrowserInfo(log.user_agent),
        module: module,
        details: {
          method: log.method,
          path: log.path,
          response_code: log.response_code,
          user_id: log.user_id,
          entite_id: log.entite_id,
          request_body: log.request_body,
          response_body: log.response_body,
          created_at: log.created_at,
          updated_at: log.updated_at
        }
      };
    });
  };

  // Charger les logs depuis l'API
  const fetchLogsFromAPI = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getAuthToken();
      if (!token) {
        setUseMockData(true);
        setLogs(demoLogs);
        setPagination({
          current_page: 1,
          last_page: 1,
          per_page: itemsPerPage,
          total: demoLogs.length
        });
        setLoading(false);
        return;
      }
      
      // Construction de l'URL avec les paramètres
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('per_page', itemsPerPage);
      
      // Filtres
      if (filterText) params.append('search', filterText);
      if (selectedType !== 'all') params.append('type', selectedType);
      if (selectedModule !== 'all') params.append('module', selectedModule);
      if (selectedUser !== 'all' && selectedUser !== 'Tous les utilisateurs' && selectedUser !== '') {
        params.append('user', selectedUser);
      }
      
      // Filtre date
      params.append('date_filter', dateFilter);
      if (dateFilter === 'custom') {
        if (customDateFrom) params.append('date_from', customDateFrom);
        if (customDateTo) params.append('date_to', customDateTo);
      }
      
      const url = `${API_BASE_URL}/logs?${params.toString()}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders()
       
      });
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }
      
      const result = await response.json();
      
      let logsData = [];
      let paginationData = {
        current_page: 1,
        last_page: 1,
        per_page: itemsPerPage,
        total: 0
      };
      
      if (result.data && Array.isArray(result.data)) {
        logsData = result.data;
        paginationData = {
          current_page: result.current_page || 1,
          last_page: result.last_page || 1,
          per_page: result.per_page || itemsPerPage,
          total: result.total || 0
        };
      } else if (Array.isArray(result)) {
        logsData = result;
        paginationData = {
          current_page: page,
          last_page: Math.ceil(result.length / itemsPerPage),
          per_page: itemsPerPage,
          total: result.length
        };
      }
      
      const transformedLogs = transformApiData(logsData);
      
      setLogs(transformedLogs);
      setPagination(paginationData);
      
    } catch (error) {
      console.error('Erreur lors du chargement des logs:', error);
      showNotification(`Erreur: ${error.message}`, 'danger');
      setUseMockData(true);
      setLogs(demoLogs);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: itemsPerPage,
        total: demoLogs.length
      });
    } finally {
      setLoading(false);
    }
  };

  // Rafraîchir les logs
  const refreshLogs = () => {
    fetchLogsFromAPI(1);
    showNotification('Rafraîchissement des logs...', 'info');
  };

  // Initialiser les données
  useEffect(() => {
    fetchLogsFromAPI(1);
  }, []);

  // Recharger quand les filtres changent
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchLogsFromAPI(1);
    }, 500);
    
    return () => clearTimeout(debounceTimer);
  }, [filterText, selectedType, selectedModule, selectedUser, itemsPerPage, dateFilter, customDateFrom, customDateTo]);

  // Fonctions pour les notifications
  const showNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    const notification = {
      id,
      message,
      type,
      show: true
    };
    
    setNotifications(prev => [notification, ...prev]);
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, duration);
  };

  const showAlert = (message, type = 'warning', duration = 5000) => {
    setAlertMessage({ show: true, message, type });
    
    if (duration > 0) {
      setTimeout(() => {
        setAlertMessage({ show: false, message: '', type: 'info' });
      }, duration);
    }
  };

  // Filtrer et trier les logs (côté client pour les données de démo)
  const filteredAndSortedItems = useMemo(() => {
    let filtered = [...logs];
    
    // Tri
    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (sortField === 'date') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [logs, sortField, sortDirection]);

  // Statistiques
  const stats = useMemo(() => {
    const total = pagination.total;
    const aujourdhui = logs.filter(log => {
      const today = new Date();
      const logDate = new Date(log.date);
      return logDate.toDateString() === today.toDateString();
    }).length;
    const cetteSemaine = logs.filter(log => {
      const now = new Date();
      const logDate = new Date(log.date);
      const diffTime = Math.abs(now - logDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 7;
    }).length;
    const erreurs = logs.filter(log => log.type === 'danger').length;
    const succes = logs.filter(log => log.type === 'success').length;
    const utilisateursUniques = [...new Set(logs.map(log => log.utilisateur))].length;
    
    return { 
      total, 
      aujourdhui, 
      cetteSemaine, 
      erreurs, 
      succes,
      utilisateursUniques
    };
  }, [logs, pagination.total]);

  // Pagination
  const totalPages = pagination.last_page;
  
  // Les items courants sont directement les logs (l'API gère la pagination)
  const currentItems = logs;

  // Fonctions de tri
  const handleSort = (field) => {
    if (loading) return;
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Gestion des sélections
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLogs(currentItems.map(log => log.id));
    } else {
      setSelectedLogs([]);
    }
  };

  const handleSelectLog = (logId) => {
    setSelectedLogs(prev => 
      prev.includes(logId) 
        ? prev.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedLogs.length === currentItems.length;
  const isSomeSelected = selectedLogs.length > 0 && selectedLogs.length < currentItems.length;

  // Voir les détails
  const viewDetails = (log) => {
    setLogDetails(log);
    setShowDetailsModal(true);
  };

  // Gestion du changement de page
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || loading) return;
    fetchLogsFromAPI(newPage);
  };

  // Formatage des dates
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    
    if (diffMinutes < 1) return "À l'instant";
    if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
    if (diffHours < 24) return `Il y a ${diffHours} h`;
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtenir la couleur du type
  const getTypeColor = (type) => {
    const colors = {
      success: 'success',
      warning: 'warning',
      danger: 'danger',
      info: 'info'
    };
    return colors[type] || 'secondary';
  };

  // Obtenir l'icône du type
  const getTypeIcon = (type) => {
    const icons = {
      success: <CheckCircle />,
      warning: <ExclamationCircle />,
      danger: <XCircle />,
      info: <InfoCircle />
    };
    return icons[type] || <InfoCircle />;
  };

  // Obtenir l'icône du module
  const getModuleIcon = (moduleName) => {
    const module = modules.find(m => m.value === moduleName);
    return module ? module.icon : <Gear />;
  };

  // Obtenir le badge de module
  const getModuleBadge = (moduleName) => {
    const module = modules.find(m => m.value === moduleName);
    if (!module) return null;
    
    return (
      <Badge bg="light" text="dark" className="fw-medium">
        {module.label}
      </Badge>
    );
  };

  // Composant pour l'en-tête triable
  const SortableHeader = ({ field, children }) => (
    <th 
      style={{ cursor: loading ? 'not-allowed' : 'pointer', userSelect: 'none' }}
      onClick={() => !loading && handleSort(field)}
      className="position-relative"
    >
      {children}
      {sortField === field && (
        <span className="ms-1">
          {sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      )}
    </th>
  );

  return (
    <React.Fragment>
      <Header />
      
      <div className="main main-app p-3 p-lg-4">
        {/* Container pour les notifications Toast */}
        <ToastContainer 
          className="p-3"
          style={{ 
            zIndex: 9999,
            position: 'fixed',
            top: '20px',
            right: '20px'
          }}
        >
          {notifications.map((notification) => (
            <Toast
              key={notification.id}
              show={notification.show}
              onClose={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
              delay={8000}
              autohide
              className={`bg-${notification.type === 'success' ? 'success' : 
                         notification.type === 'danger' ? 'danger' :
                         notification.type === 'warning' ? 'warning' : 'info'} text-white`}
            >
              <Toast.Header>
                {notification.type === 'success' ? <CheckCircle className="me-2" /> :
                 notification.type === 'danger' ? <XCircle className="me-2" /> :
                 notification.type === 'warning' ? <ExclamationCircle className="me-2" /> :
                 <InfoCircle className="me-2" />}
                <strong className="me-auto">
                  {notification.type === 'success' ? 'Succès' : 
                   notification.type === 'danger' ? 'Erreur' :
                   notification.type === 'warning' ? 'Attention' : 'Information'}
                </strong>
                <small>{new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
              </Toast.Header>
              <Toast.Body className="text-white">
                {notification.message}
              </Toast.Body>
            </Toast>
          ))}
        </ToastContainer>

        {/* Alerte Bootstrap */}
        {alertMessage.show && (
          <div className="container-fluid mb-3">
            <Alert 
              variant={alertMessage.type} 
              dismissible 
              onClose={() => setAlertMessage({ show: false, message: '', type: 'info' })}
              className="shadow-sm border-0"
            >
              <div className="d-flex align-items-center">
                {alertMessage.type === 'success' ? <CheckCircle className="me-2 fs-5" /> :
                 alertMessage.type === 'danger' ? <XCircle className="me-2 fs-5" /> :
                 alertMessage.type === 'warning' ? <ExclamationCircle className="me-2 fs-5" /> :
                 <InfoCircle className="me-2 fs-5" />}
                <span className="fw-medium">{alertMessage.message}</span>
              </div>
            </Alert>
          </div>
        )}

        <div className="container-fluid">
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item"><Link to="/admin/dashboard">Tableau de bord</Link></li>
                    <li className="breadcrumb-item active" aria-current="page">Logs système</li>
                  </ol>
                  <h4 className="main-title mb-0">
                    <ClockHistory className="me-2" />
                    Logs Système
                  </h4>
                  <p className="text-muted mb-0">
                    Journal d'activités et d'événements système
                    {useMockData && " (mode démonstration)"}
                  </p>
                </div>

                <div className="d-flex gap-2 flex-wrap">
                  {selectedLogs.length > 0 && (
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => setShowBulkDeleteModal(true)}
                      disabled={loading}
                    >
                      <Trash className="me-2" size={16} />
                      Supprimer ({selectedLogs.length})
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline-success"
                    onClick={refreshLogs}
                    className="d-flex align-items-center"
                    disabled={loading}
                  >
                    <ArrowRepeat className={`me-2 ${loading ? 'spin' : ''}`} size={16} />
                    Rafraîchir
                  </Button>
                  
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => navigate(-1)}
                    className="d-flex align-items-center"
                  >
                    <ArrowLeft className="me-2" size={16} />
                    Retour
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Cartes de statistiques */}
          <Row className="g-3 mb-4">
            <Col md={6} lg={3}>
              <Card className="border-0 shadow-sm overflow-hidden stats-card">
                <div className="card-gradient bg-primary"></div>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="text-uppercase text-muted mb-2">Total Logs</h6>
                      <h2 className="mb-0">{pagination.total}</h2>
                      <div className="mt-2">
                        <Badge bg="primary-subtle" text="primary" className="fw-medium">
                          <ClockHistory className="me-1" size={12} />
                          Toutes activités
                        </Badge>
                      </div>
                    </div>
                    <div className="icon-container bg-primary-subtle">
                      <Database className="text-primary" size={28} />
                    </div>
                  </div>
                  <div className="progress-wrapper mt-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small className="text-muted">Aujourd'hui</small>
                      <small className="fw-medium">{stats.aujourdhui}</small>
                    </div>
                    <ProgressBar 
                      now={pagination.total > 0 ? (stats.aujourdhui / pagination.total) * 100 : 0} 
                      variant="primary"
                      className="stats-progress"
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={6} lg={3}>
              <Card className="border-0 shadow-sm overflow-hidden stats-card">
                <div className="card-gradient bg-success"></div>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="text-uppercase text-muted mb-2">Succès</h6>
                      <h2 className="mb-0">{stats.succes}</h2>
                      <div className="mt-2">
                        <Badge bg="success-subtle" text="success" className="fw-medium">
                          <CheckCircle className="me-1" size={12} />
                          Actions réussies
                        </Badge>
                      </div>
                    </div>
                    <div className="icon-container bg-success-subtle">
                      <CheckCircle className="text-success" size={28} />
                    </div>
                  </div>
                  <div className="progress-wrapper mt-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small className="text-muted">Taux de succès</small>
                      <small className="fw-medium">
                        {pagination.total > 0 ? ((stats.succes / pagination.total) * 100).toFixed(1) : 0}%
                      </small>
                    </div>
                    <ProgressBar 
                      now={pagination.total > 0 ? (stats.succes / pagination.total) * 100 : 0} 
                      variant="success"
                      className="stats-progress"
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={6} lg={3}>
              <Card className="border-0 shadow-sm overflow-hidden stats-card">
                <div className="card-gradient bg-danger"></div>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="text-uppercase text-muted mb-2">Erreurs</h6>
                      <h2 className="mb-0">{stats.erreurs}</h2>
                      <div className="mt-2">
                        <Badge bg="danger-subtle" text="danger" className="fw-medium">
                          <XCircle className="me-1" size={12} />
                          Problèmes détectés
                        </Badge>
                      </div>
                    </div>
                    <div className="icon-container bg-danger-subtle">
                      <ShieldExclamation className="text-danger" size={28} />
                    </div>
                  </div>
                  <div className="progress-wrapper mt-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small className="text-muted">Cette semaine</small>
                      <small className="fw-medium">{stats.cetteSemaine}</small>
                    </div>
                    <ProgressBar 
                      now={pagination.total > 0 ? (stats.cetteSemaine / pagination.total) * 100 : 0} 
                      variant="danger"
                      className="stats-progress"
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={6} lg={3}>
              <Card className="border-0 shadow-sm overflow-hidden stats-card">
                <div className="card-gradient bg-info"></div>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="text-uppercase text-muted mb-2">Utilisateurs</h6>
                      <h2 className="mb-0">{stats.utilisateursUniques}</h2>
                      <div className="mt-2">
                        <Badge bg="info-subtle" text="info" className="fw-medium">
                          <People className="me-1" size={12} />
                          Actifs
                        </Badge>
                      </div>
                    </div>
                    <div className="icon-container bg-info-subtle">
                      <Person className="text-info" size={28} />
                    </div>
                  </div>
                  <div className="progress-wrapper mt-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small className="text-muted">Actions par user</small>
                      <small className="fw-medium">
                        {stats.utilisateursUniques > 0 ? Math.round(pagination.total / stats.utilisateursUniques) : 0}
                      </small>
                    </div>
                    <ProgressBar 
                      now={75} 
                      variant="info"
                      className="stats-progress"
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Barre de recherche et filtres */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={4}>
                      <InputGroup>
                        <InputGroup.Text>
                          <Search />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Rechercher dans les logs..."
                          value={filterText}
                          onChange={(e) => setFilterText(e.target.value)}
                          disabled={loading}
                        />
                        {filterText && (
                          <Button 
                            variant="outline-secondary" 
                            onClick={() => setFilterText('')}
                          >
                            <X size={14} />
                          </Button>
                        )}
                      </InputGroup>
                    </Col>
                    
                    <Col md={3}>
                      <Form.Select
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                        disabled={loading}
                      >
                        {logTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    
                    <Col md={3}>
                      <Form.Select
                        value={selectedModule}
                        onChange={(e) => setSelectedModule(e.target.value)}
                        disabled={loading}
                      >
                        {modules.map(module => (
                          <option key={module.value} value={module.value}>{module.label}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    
                    <Col md={2}>
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setFilterText("");
                          setSelectedType("all");
                          setSelectedModule("all");
                          setSelectedUser("all");
                          setDateFilter("today");
                          setCustomDateFrom("");
                          setCustomDateTo("");
                        }}
                        className="w-100"
                        disabled={loading}
                      >
                        <X className="me-2" />
                        Réinitialiser
                      </Button>
                    </Col>
                  </Row>
                  
                  {/* Deuxième ligne: filtre date */}
                  <Row className="g-3 mt-3">
                    <Col md={3}>
                      <Form.Select
                        value={dateFilter}
                        onChange={(e) => {
                          setDateFilter(e.target.value);
                          setShowCustomDate(e.target.value === 'custom');
                        }}
                        disabled={loading}
                      >
                        {dateFilters.map(filter => (
                          <option key={filter.value} value={filter.value}>{filter.label}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    
                    {showCustomDate && (
                      <>
                        <Col md={3}>
                          <Form.Control
                            type="date"
                            placeholder="Date début"
                            value={customDateFrom}
                            onChange={(e) => setCustomDateFrom(e.target.value)}
                            disabled={loading}
                          />
                        </Col>
                        <Col md={3}>
                          <Form.Control
                            type="date"
                            placeholder="Date fin"
                            value={customDateTo}
                            onChange={(e) => setCustomDateTo(e.target.value)}
                            disabled={loading}
                          />
                        </Col>
                      </>
                    )}
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tableau principal */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-3">
                  <div className="d-flex align-items-center">
                    <ClockHistory className="me-2" />
                    <span className="fw-semibold">Journal d'Activités ({pagination.total} total)</span>
                    {loading && (
                      <Spinner size="sm" className="ms-2" animation="border" variant="light" />
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-light" size="sm" disabled={loading}>
                        {itemsPerPage} par page
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => { setItemsPerPage(10); fetchLogsFromAPI(1); }}>10 par page</Dropdown.Item>
                        <Dropdown.Item onClick={() => { setItemsPerPage(15); fetchLogsFromAPI(1); }}>15 par page</Dropdown.Item>
                        <Dropdown.Item onClick={() => { setItemsPerPage(25); fetchLogsFromAPI(1); }}>25 par page</Dropdown.Item>
                        <Dropdown.Item onClick={() => { setItemsPerPage(50); fetchLogsFromAPI(1); }}>50 par page</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                    <div className="text-white-50 small d-flex align-items-center">
                      {selectedLogs.length > 0 && (
                        <span>{selectedLogs.length} sélectionné(s)</span>
                      )}
                    </div>
                  </div>
                </Card.Header>
                
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '40px' }}>
                            {!loading && (
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  style={{
                                    borderRadius: '50%',
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer'
                                  }}
                                  checked={isAllSelected}
                                  ref={input => {
                                    if (input) {
                                      input.indeterminate = isSomeSelected;
                                    }
                                  }}
                                  onChange={handleSelectAll}
                                />
                              </div>
                            )}
                          </th>
                          <th style={{ width: '50px' }}>Type</th>
                          <SortableHeader field="action">Action</SortableHeader>
                          <th>Description</th>
                          <th style={{ width: '120px' }}>Module</th>
                          <SortableHeader field="utilisateur">Utilisateur</SortableHeader>
                          <SortableHeader field="date" style={{ width: '150px' }}>Date/Heure</SortableHeader>
                          <th style={{ width: '90px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="8" className="text-center py-5">
                              <div className="d-flex justify-content-center align-items-center">
                                <Spinner animation="border" variant="primary" className="me-3" />
                                <span className="text-muted">Chargement des logs...</span>
                              </div>
                            </td>
                          </tr>
                        ) : currentItems.length > 0 ? (
                          currentItems.map((log) => (
                            <tr 
                              key={log.id}
                              style={{
                                borderLeft: `4px solid var(--bs-${getTypeColor(log.type)})`
                              }}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  style={{
                                    borderRadius: '50%',
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer'
                                  }}
                                  checked={selectedLogs.includes(log.id)}
                                  onChange={() => handleSelectLog(log.id)}
                                />
                              </td>
                              <td>
                                <div className={`text-${getTypeColor(log.type)}`} title={logTypes.find(t => t.value === log.type)?.label}>
                                  {getTypeIcon(log.type)}
                                </div>
                              </td>
                              <td>
                                <strong className="d-block">{log.action}</strong>
                                <small className="text-muted d-none d-md-block">
                                  IP: {log.ip}
                                </small>
                              </td>
                              <td>
                                <div className="text-truncate" style={{ maxWidth: '300px' }}>
                                  {log.description}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <span className="me-2">{getModuleIcon(log.module)}</span>
                                  {getModuleBadge(log.module)}
                                </div>
                              </td>
                              <td>
                                <div>
                                  <strong className="d-block text-truncate" style={{ maxWidth: '120px' }}>{log.utilisateur}</strong>
                                  <small className="text-muted d-none d-md-block">
                                    {log.navigateur}
                                  </small>
                                </div>
                              </td>
                              <td>
                                <div>
                                  <small className="d-block">{formatDate(log.date)}</small>
                                  <small className="text-muted d-none d-md-block">
                                    <Calendar size={12} className="me-1" />
                                    {formatDateTime(log.date)}
                                  </small>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    title="Voir les détails"
                                    onClick={() => viewDetails(log)}
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: '32px', height: '32px' }}
                                  >
                                    <Eye size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-5">
                              <ClockHistory size={48} className="text-muted" />
                              <p className="mt-3 text-muted">AUCUN LOG TROUVÉ.</p>
                              <p className="text-muted mb-3">
                                {filterText || selectedType !== 'all' || selectedModule !== 'all' || selectedUser !== 'all' || dateFilter !== 'today'
                                  ? "Aucun log ne correspond aux critères de recherche."
                                  : "Aucune activité enregistrée pour le moment."}
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="p-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div className="text-muted small">
                        Affichage de {(pagination.current_page - 1) * pagination.per_page + 1} à {Math.min(pagination.current_page * pagination.per_page, pagination.total)} sur {pagination.total} logs
                        {selectedLogs.length > 0 && (
                          <span className="ms-2 text-primary">• {selectedLogs.length} sélectionné(s)</span>
                        )}
                      </div>
                      <Pagination className="mb-0">
                        <Pagination.Prev 
                          disabled={pagination.current_page === 1 || loading}
                          onClick={() => handlePageChange(pagination.current_page - 1)}
                        />
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (pagination.current_page <= 3) {
                            pageNum = i + 1;
                          } else if (pagination.current_page >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = pagination.current_page - 2 + i;
                          }
                          
                          if (pageNum >= 1 && pageNum <= totalPages) {
                            return (
                              <Pagination.Item
                                key={pageNum}
                                active={pageNum === pagination.current_page}
                                onClick={() => handlePageChange(pageNum)}
                                disabled={loading}
                              >
                                {pageNum}
                              </Pagination.Item>
                            );
                          }
                          return null;
                        })}
                        <Pagination.Next 
                          disabled={pagination.current_page === totalPages || loading}
                          onClick={() => handlePageChange(pagination.current_page + 1)}
                        />
                      </Pagination>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* Modal de détails */}
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered>
          {logDetails && (
            <>
              <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title>
                  <Eye className="me-2" />
                  Détails du log
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <ListGroup variant="flush">
                  <ListGroup.Item className="d-flex justify-content-between">
                    <strong>Action:</strong> {logDetails.action}
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between">
                    <strong>Description:</strong> {logDetails.description}
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between">
                    <strong>Type:</strong> 
                    <Badge bg={getTypeColor(logDetails.type)}>{logDetails.type}</Badge>
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between">
                    <strong>Module:</strong> {modules.find(m => m.value === logDetails.module)?.label}
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between">
                    <strong>Utilisateur:</strong> {logDetails.utilisateur}
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between">
                    <strong>IP:</strong> {logDetails.ip}
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between">
                    <strong>Navigateur:</strong> {logDetails.navigateur}
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between">
                    <strong>Date:</strong> {formatDateTime(logDetails.date)}
                  </ListGroup.Item>
                </ListGroup>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
                  Fermer
                </Button>
              </Modal.Footer>
            </>
          )}
        </Modal>

        {/* Modal de suppression multiple */}
        <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)} centered>
          <Modal.Header closeButton className="bg-danger text-white">
            <Modal.Title>
              <Trash className="me-2" />
              Confirmer la suppression
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Êtes-vous sûr de vouloir supprimer <strong>{selectedLogs.length}</strong> log(s) ?</p>
            <Alert variant="warning">
              <ExclamationCircle className="me-2" />
              Cette action est irréversible.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowBulkDeleteModal(false)}>
              Annuler
            </Button>
            <Button variant="danger" onClick={() => {
              setSelectedLogs([]);
              setShowBulkDeleteModal(false);
              showNotification(`${selectedLogs.length} log(s) supprimé(s)`, 'success');
            }}>
              Confirmer la suppression
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>

      <style>{`
        .main-app {
          background-color: #f8f9fa;
          min-height: calc(100vh - 60px);
        }
        
        .icon-container {
          width: 50px;
          height: 50px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .stats-progress {
          height: 6px;
          border-radius: 3px;
        }
        
        .modal-details .modal-header {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }
        
        .breadcrumb {
          background: transparent;
          padding: 0;
          margin-bottom: 0.5rem;
        }
        
        .breadcrumb-item a {
          color: var(--bs-primary);
          text-decoration: none;
        }
        
        .breadcrumb-item.active {
          color: var(--bs-secondary);
        }
        
        .main-title {
          color: var(--bs-dark);
          font-weight: 600;
        }
        
        .stats-card {
          position: relative;
        }
        
        .card-gradient {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @media (max-width: 768px) {
          .main-app {
            padding: 1rem !important;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 1rem;
          }
          
          .d-flex.gap-2 {
            flex-wrap: wrap;
          }
          
          .table-responsive {
            font-size: 0.875rem;
          }
        }
      `}</style>
    </React.Fragment>
  );
}